import { useState } from 'react'

import { Check } from 'lucide-react'

import { AccountRow } from '~/app/accounts/types'
import { CurrencyAmount, Tooltip } from '~/components/component-library'
import { useSmartAccountBalances } from '~/hooks/useBalances'

const AUTOMATION_RUN_INTERVAL_MS = 10 * 60 * 1000
const DEFAULT_AUTOMATION_THRESHOLD_USD = 5 // mirrors the server-side default

export const AutomationAccountStatus = ({
  account,
}: {
  account: AccountRow
}) => {
  if (!account.automation) return null

  return <AutomationAccountProgress account={account} />
}

const AutomationAccountProgress = ({ account }: { account: AccountRow }) => {
  const { totalValue, isLoading } = useSmartAccountBalances(account.address)
  const [runEstimate, setRunEstimate] = useState('Will run in a few minutes')
  const threshold =
    account.automation?.policy.minimumThresholdUsd ??
    DEFAULT_AUTOMATION_THRESHOLD_USD
  const progress =
    threshold > 0
      ? Math.min(Math.max((totalValue / threshold) * 100, 0), 100)
      : 0
  const hasReachedThreshold = totalValue > threshold

  return (
    <div className="flex w-40 flex-col gap-1.5">
      <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full rounded-full bg-green-500 transition-[width]"
          style={{ width: `${isLoading ? 0 : progress}%` }}
        />
      </div>
      <div className="flex items-center justify-between gap-3 text-sm leading-4">
        <div className="flex items-center gap-1 font-medium">
          <CurrencyAmount
            value={totalValue}
            isLoading={isLoading}
            layout="inline"
          />
          <span className="text-secondary">
            / <CurrencyAmount value={threshold} layout="inline" />
          </span>
        </div>
        {hasReachedThreshold ? (
          <Tooltip
            content={runEstimate}
            onOpenChange={(isOpen) => {
              if (isOpen) setRunEstimate(getNextAutomationRunEstimate())
            }}
          >
            <div className="flex size-3.5 items-center justify-center rounded-full bg-green-500">
              <Check className="size-2 text-white" strokeWidth={4} />
            </div>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

const getNextAutomationRunEstimate = () => {
  const nowMs = Date.now()
  const remainder = nowMs % AUTOMATION_RUN_INTERVAL_MS
  const msUntilNextRun =
    remainder === 0 ? 0 : AUTOMATION_RUN_INTERVAL_MS - remainder
  const minutesUntilNextRun = Math.ceil(msUntilNextRun / 60_000)

  if (minutesUntilNextRun <= 1) return 'Will run in less than 1 minute'

  return `Will run in ${minutesUntilNextRun} minutes`
}
